import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Share, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import DocumentViewer from '../../components/DocumentViewer';

const MaterialViewPage = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  // Fetch material details
  useEffect(() => {
    const fetchMaterial = async () => {
      if (!materialId) {
        setError('Material ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${apiBaseUrl}/api/materials/${materialId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Material not found');
          } else if (response.status === 403) {
            setError('You do not have permission to view this material');
          } else {
            setError('Failed to load material');
          }
          return;
        }

        const materialData = await response.json();
        
        // Debug: Log material data to compare with modal version  
        console.log('Full-screen material data:', materialData);
        
        setMaterial(materialData);
        
        // Track the view
        trackMaterialView(materialId);
      } catch (err) {
        console.error('Error fetching material:', err);
        setError('Failed to load material');
      } finally {
        setLoading(false);
      }
    };

    fetchMaterial();
  }, [materialId, apiBaseUrl]);

  // Track material view
  const trackMaterialView = async (materialId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/materials/${materialId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        console.log(`Successfully tracked view for material ${materialId}`);
      }
    } catch (error) {
      console.error('Error tracking material view:', error);
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: material?.title || 'Material',
          text: material?.description || 'Check out this material',
          url: url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          fallbackShare(url);
        }
      }
    } else {
      fallbackShare(url);
    }
  };

  const fallbackShare = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader className="h-12 w-12 text-red-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading material...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
          </div>

          {/* Error message */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <div className="text-red-500 mb-4">
              <ExternalLink className="h-16 w-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Material Not Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <div className="space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Go Back
              </button>
              <Link
                to="/search"
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Search Materials
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Material not found
  if (!material) {
    return (
      <div className="fixed inset-y-0 left-64 right-0 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Material Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              The requested material could not be found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-y-0 left-64 right-0 bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <div className="flex items-center space-x-2">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {material.title}
              </h1>
              {material.courseCode && material.courseName && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  in {material.courseCode}: {material.courseName}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleShare}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
              title="Share material"
            >
              <Share className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      <div className="flex-grow relative">
        <DocumentViewer
          onClose={() => navigate(-1)}
          title={material.title}
          fileType={material.fileType}
          fileUrl={material.fileUrl}
          token={localStorage.getItem('token')}
          materialId={material.id}
          currentUser={user}
          sidebarWidth={0}
          isFullScreenPage={true} // Indicate this is used in a full-screen page
        />
      </div>
    </div>
  );
};

export default MaterialViewPage;